// src/pages/UploadCardContent.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingScreen from '../components/LoadingScreen';
import EvoWishesLogo from '../assets/evo_wishes.png';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://192.168.0.146:8000/api';
const AUTH_KEY = 'authenticated_card'; // New key for successful auth

function CameraIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8h4l2-2h6l2 2h4 m-2 12H5a2 2 0 01-2-2V8 a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2z" />
      <circle cx="12" cy="13" r="3" />
    </svg>
  );
}

function UploadIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 9l5-5m0 0l5 5m-5-5v12" />
    </svg>
  );
}
function VideoIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );
}
const UploadCardContent = () => {
  const { card_uid } = useParams();
  const navigate = useNavigate();

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true); // For initial loading screen
  const [cardData, setCardData] = useState(null); // For front_picture background

  const cameraInputRef = useRef(null); // Ref for camera input
  const uploadInputRef = useRef(null); // Ref for upload input
  const [frontPicture, setFrontPicture] = useState(null); // For LoadingScreen background
  const [uploadSuccess, setUploadSuccess] = useState(false); // New state for success notification


  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000); // 3-second loading delay

    if (localStorage.getItem(`${AUTH_KEY}_${card_uid}`) !== 'true') {
      navigate(`/card_uid/${card_uid}`); // Redirect to CheckCard if not authenticated
      return;
    }
    // Fetch card data for background
    axios
      .get(`${API_BASE_URL}/card_uid/${card_uid}/`)
      .then((response) => {
        setCardData(response.data);
        if (response.data.front_picture) {
          setFrontPicture(`${API_BASE_URL.replace('/api', '')}${response.data.front_picture}`);
        }
      if (response.data.video) {
        //navigate(`/card_uid/${card_uid}`); // Redirect to CheckCard if not authenticated
        //return;
      }

      })
      .catch((err) => {
        console.error('Error fetching card data:', err);
      });

    return () => clearTimeout(timer);
  }, [card_uid]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) { // Example: Limit to 100MB
        setError("File is too large (max 100MB).");
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setShowModal(true);
      setError(null);
    }
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append("video", selectedFile);
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    try {
      await axios.post(`${API_BASE_URL}/upload_video/${card_uid}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setUploadProgress(percent);
        },
      });
      setUploading(false);
      setShowModal(false);
      setUploadSuccess(true); // Show success notification
    } catch (err) {
      console.error('Full upload error:', err);
      const errMsg = err.response?.data?.error || err.message || 'Unknown error during upload';
      setError(`Error uploading video: ${errMsg}`);
    } finally {
      setUploading(false);
      setShowModal(false);
    }
  };

  const handleGoToCard = () => {
    navigate(`/video/${card_uid}`);
  };

  const handleCancelUpload = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    setPreviewUrl("");
    setShowModal(false);

    // Reset file inputs to allow re-selection
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
    }
  };

  if (loading) return <LoadingScreen backgroundImage={frontPicture} />;

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4" 
      style={{ 
        backgroundImage: cardData && cardData.front_picture ? `url(${API_BASE_URL.replace('/api', '')}${cardData.front_picture})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {!uploadSuccess && (
        <div className="w-full max-w-md bg-white bg-opacity-50 backdrop-filter backdrop-blur-md rounded-lg shadow-lg p-6 border border-white/30"> {/* Glassy look: transparent, blur, subtle border */}
          <div className="flex justify-center mb-4">
            <img src={EvoWishesLogo} alt="EvoWishes Logo" className="h-16 w-auto" />
          </div>
          <p className="text-center text-gray-600 mb-2">Картичка: <span className="font-semibold">{card_uid}</span></p>
          <p className="text-center text-gray-600 mb-6">Моля, заснемете или качете персоналното видео.</p>
          <input id="uploadInput" ref={uploadInputRef} type="file" accept="video/*" className="hidden" onChange={handleFileSelect} />
          <div className="flex justify-center gap-4">
            <button onClick={() => document.getElementById("uploadInput").click()} className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white font-semibold rounded-full shadow hover:bg-green-600 transition-colors">
              <UploadIcon className="w-5 h-5" />
              <span>Качи файл</span>
            </button>
          </div>
          {error && <div className="mt-4 text-red-600 text-center">{error}</div>}
        </div>
      )}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md bg-white bg-opacity-50 backdrop-filter backdrop-blur-md rounded-lg shadow-lg p-6 border border-white/30"> {/* Glassy look: transparent, blur, subtle border */}
            <h2 className="text-lg font-semibold text-gray-600 mb-2">Преглед на видеото</h2>
            <video src={previewUrl} controls className="w-full h-auto mb-4 rounded" />
            <div className="flex justify-end space-x-2">
              <button onClick={handleConfirmUpload} className="px-4 py-2 bg-green-500 text-white font-semibold rounded hover:bg-green-600" disabled={uploading}>
                Качи
              </button>
              <button onClick={handleCancelUpload} className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">Отказ</button>
            </div>
          </div>
        </div>
      )}
      {uploadSuccess && (
         <div className="fixed  inset-0 z-50 flex items-center p-4  justify-center bg-black bg-opacity-50">
        <div className="w-full max-w-md bg-white bg-opacity-50 backdrop-filter backdrop-blur-md rounded-lg shadow-lg p-6 border border-white/30"> {/* Glassy look: transparent, blur, subtle border */}
        <div className="flex justify-center mt-3 mb-10">
          <img src={EvoWishesLogo} alt="EvoWishes Logo" className="h-16 w-auto" />
        </div>
        <p className="text-center text-fontweght-500 text-gray-600 mb-12">Вашето видео е качено успешно!</p>
        <div className="flex justify-center gap-4 mb-7">
          <button onClick={handleGoToCard} className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white font-semibold rounded-full shadow hover:bg-red-600 transition-colors">
            <VideoIcon className="w-5 h-5" />
            <span>Продължи</span>
          </button>
        </div>
      </div>
      </div>

      )}
    </div>
  );
};

export default UploadCardContent;